/**
 * "Decline <company>'s invitation?" — asked before a decline, because a
 * decline is terminal and the company sees it. One dialog for every place an
 * invitation can be declined from (Home's card, the bell's Invitations tab).
 */
import { useTranslation } from '@tonnie/locale';
import { useDeclineInvitation, type MyPendingInvitation } from '@tonnie/sdk';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  buttonVariants,
} from '@tonnie/ui';

export function DeclineInvitationDialog({
  invitation,
  open,
  onOpenChange,
  decline,
}: {
  invitation: MyPendingInvitation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The caller's mutation, so it can show the pending and failed states. */
  decline: ReturnType<typeof useDeclineInvitation>;
}) {
  const { t } = useTranslation();
  const company = invitation.companyName;
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="invitation-decline-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('invitations.card.declineTitle', { company })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('invitations.card.declineBody', { company })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('invitations.card.declineCancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => decline.mutate(invitation.token)}
            className={buttonVariants({ variant: 'destructive' })}
            data-testid="invitation-decline-confirm-action"
          >
            {t('invitations.card.declineConfirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
